import React, { useMemo, useState } from "react";
import PageTitle from "../../shared/components/pagetitle";
import { CLOCK_ICON, NOTIFICATION_ICON, SEARCH_INPUT_ICON } from "../../assets";
import clsx from "clsx";
import { useQuery } from "react-query";
import { getCourtNotifications } from "../../Services/court.services";
import { formatTimeElapsed } from "../../utils";

interface Notification {
  id: number;
  user_id: string;
  title: string;
  body: string;
  type: string;
  meta_data: string;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

function Notifications() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const { data: notifications = [], isLoading } = useQuery(
    "notifications",
    getCourtNotifications,
  );

  const tabs = [
    { label: "All Notifications", tab: "all" },
    { label: "Unread", tab: "unread" },
  ];

  // 🔥 Combine tab + search filtering
  const filteredNotifications = useMemo(() => {
    let result: Notification[] = notifications;

    if (activeTab === "unread") {
      result = result.filter((item) => !item.read_at);
    }

    if (searchQuery.trim()) {
      result = result.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    return result;
  }, [notifications, activeTab, searchQuery]);

  return (
    <div className="p-3 sm:p-5">
      <PageTitle
        title="Notifications"
        subtitle="Stay updated on all searches, case activities, and system alerts in real-time."
      />

      <div className="flex items-start flex-col gap-y-4 w-full mt-5">
        {/* Search */}
        <div className="w-full sm:w-fit items-center gap-x-2 flex px-3 py-2 rounded-xl border border-borderColor">
          <img
            src={SEARCH_INPUT_ICON}
            className="w-4 h-4 sm:w-[20px] sm:h-[20px] flex-shrink-0"
            alt=""
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-transparent placeholder:text-sm outline-none"
            placeholder="Search notification"
          />
        </div>

        <div className="border border-borderColor w-full rounded-xl overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-borderColor px-3 sm:px-5 w-full pt-2 flex flex-col sm:flex-row justify-between gap-4">
            <div className="border-b flex items-center overflow-x-auto">
              {tabs.map((item, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(item.tab)}
                  className={clsx(
                    "text-[#656F7D] font-medium text-sm px-3 sm:px-4 py-2 whitespace-nowrap",
                    item.tab === activeTab &&
                      "border-b-2 border-globalGold text-globalGold",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button className="text-sm text-globalGold font-medium whitespace-nowrap">
              Mark all as read
            </button>
          </div>

          {/* Content */}
          <div className="mt-3 flex flex-col gap-y-3 w-full px-3 pb-4">
            {/* 🔄 Loading */}
            {isLoading && (
              <div className="w-full py-10 text-center text-sm text-primary-50">
                Loading notifications...
              </div>
            )}

            {/* 📭 Empty State */}
            {!isLoading && filteredNotifications.length === 0 && (
              <div className="w-full flex flex-col items-center justify-center py-12 text-center">
                <img
                  src={NOTIFICATION_ICON}
                  className="w-10 h-10 opacity-40 mb-3"
                  alt=""
                />
                <h3 className="text-sm font-medium text-[#2A2E34]">
                  No notifications found
                </h3>
                <p className="text-xs text-primary-50 mt-1 max-w-xs">
                  {searchQuery
                    ? "No notification matches your search."
                    : activeTab === "unread"
                      ? "You have no unread notifications."
                      : "You're all caught up! No notifications yet."}
                </p>
              </div>
            )}

            {/* 📩 Notifications List */}
            {!isLoading &&
              filteredNotifications.map((item) => (
                <div
                  key={item.id}
                  className={clsx(
                    "w-full flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 gap-3 rounded-lg",
                    !item.read_at && "bg-yellow-50",
                  )}
                >
                  <div className="flex items-start sm:items-center gap-x-2 w-full">
                    <img
                      src={NOTIFICATION_ICON}
                      className="w-8 h-8 sm:w-[40px] sm:h-[40px] flex-shrink-0"
                      alt=""
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[#2A2E34] font-medium text-sm break-words">
                        {item.title}
                      </span>
                      <span className="text-xs font-normal text-primary-50 break-words">
                        {item.body}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-x-2 flex-shrink-0">
                    <img
                      src={CLOCK_ICON}
                      className="w-3 h-3 sm:w-[16px] sm:h-[16px]"
                      alt=""
                    />
                    <span className="text-xs font-normal text-primary-50 whitespace-nowrap">
                      {formatTimeElapsed(item.created_at)}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Notifications;
